// AI-driven conversation transcripts. Each transcript is a chronological list
// of messages. Channels: webchat, sms, whatsapp, voice, email.
// Speakers: lead, ai (Claude), agent.
//
// Sofia Martinez and Carlos Mendez transcripts are in Spanish.
// Emily Watson's transcript includes a mid-conversation handoff from AI to
// a human agent (the "escalation") when a sensitive pricing question arises.

import { daysAgo, hoursAgo, minutesAgo } from './leads.js';

export const conversations = [
  // ── SOFIA MARTINEZ · Spanish · webchat → SMS ─────────────────────────────
  {
    id: 'conv-sofia-01',
    leadId: 'lead-sofia-martinez',
    channel: 'webchat',
    language: 'es',
    startedAt: daysAgo(9),
    endedAt: daysAgo(9),
    outcome: 'qualified',
    summary:
      'Sofia confirmó presupuesto cash de $5-6M, timeline de 30-60 días, y preferencia por Coral Gables/Coconut Grove. La IA calificó como Hot y agendó seguimiento.',
    messages: [
      {
        at: daysAgo(9),
        from: 'lead',
        text: 'Hola, vi su listing en Coral Gables y quería pedir más información.',
      },
      {
        at: daysAgo(9),
        from: 'ai',
        text: '¡Hola Sofia! Soy la asistente de Smith Luxury Group. ¿Me cuenta qué zona y rango de precio está buscando?',
      },
      {
        at: daysAgo(9),
        from: 'lead',
        text: 'Coral Gables o Coconut Grove, entre $5 y $6 millones. Pago en efectivo, vendo mi casa actual en Pinecrest.',
      },
      {
        at: daysAgo(9),
        from: 'ai',
        text: '¡Excelente! ¿Cuándo planea mudarse? Y ¿cuántas habitaciones necesita?',
      },
      {
        at: daysAgo(9),
        from: 'lead',
        text: 'En 30-60 días. Mínimo 5 habitaciones, piscina indispensable.',
      },
      {
        at: daysAgo(9),
        from: 'ai',
        text: 'Perfecto. Le agendo una llamada con Natalia Ortiz, especialista en Coral Gables. ¿Mañana a las 10am o 3pm le queda mejor?',
      },
      {
        at: daysAgo(9),
        from: 'lead',
        text: '3pm me queda perfecto. Gracias.',
      },
      {
        at: daysAgo(9),
        from: 'ai',
        text: 'Confirmado. Natalia la llamará mañana a las 3pm. Le envío las tres mejores opciones por email ahora mismo.',
      },
    ],
  },

  // ── JAMES CHEN · English · email + webchat ────────────────────────────────
  {
    id: 'conv-james-01',
    leadId: 'lead-james-chen',
    channel: 'webchat',
    language: 'en',
    startedAt: daysAgo(12),
    endedAt: daysAgo(11),
    outcome: 'qualified',
    summary:
      'James confirmed SF relocation, $6-9.5M budget, pre-approved with JPMorgan. Interested in Mandarin Oriental pre-con and existing Downtown units.',
    messages: [
      {
        at: daysAgo(12),
        from: 'lead',
        text: 'Looking at Miami for a relocation from SF. Mostly curious about the Mandarin Oriental and St. Regis residences.',
      },
      {
        at: daysAgo(12),
        from: 'ai',
        text: "Welcome, James. Both are great fits — Mandarin Oriental pricing starts around $5.5M, St. Regis around $3.9M. What's your timeline and how many bedrooms?",
      },
      {
        at: daysAgo(12),
        from: 'lead',
        text: '60-90 days. Three bedrooms minimum, want a home office. Budget up to $9.5M.',
      },
      {
        at: daysAgo(12),
        from: 'ai',
        text: 'Great range — that opens both pre-con towers and some ready-to-move-in options like 1000 Biscayne. Do you have financing lined up?',
      },
      {
        at: daysAgo(12),
        from: 'lead',
        text: 'Yes, pre-approved with JPMorgan up to $8M. Would go 80% LTV.',
      },
      {
        at: daysAgo(11),
        from: 'ai',
        text: 'Perfect. I\'ll have Ana Castillo — our new-construction specialist — send you a short-list with three pre-con and two ready options. Shall I schedule a 30-min call tomorrow at 4pm PT?',
      },
      {
        at: daysAgo(11),
        from: 'lead',
        text: 'Works. Thanks.',
      },
    ],
  },

  // ── PATRICIA DUBOIS · English · whatsapp ─────────────────────────────────
  {
    id: 'conv-patricia-01',
    leadId: 'lead-patricia-dubois',
    channel: 'whatsapp',
    language: 'en',
    startedAt: daysAgo(6),
    endedAt: daysAgo(5),
    outcome: 'qualified',
    summary:
      'Patricia (Paris) wants a waterfront Miami estate, $12-20M cash. Three-day tour planned. Passed to Marco Rivera.',
    messages: [
      {
        at: daysAgo(6),
        from: 'lead',
        text: 'Hello. I would like information about waterfront estates in Miami Beach or Key Biscayne — ideally with private dockage. Budget is $15-20M, all cash.',
      },
      {
        at: daysAgo(6),
        from: 'ai',
        text: 'Bonjour Patricia. We have several excellent options — Golden Beach direct-ocean, Key Biscayne deep-water, and a Surfside PH. How many bedrooms, and any preference on modern vs classical?',
      },
      {
        at: daysAgo(6),
        from: 'lead',
        text: 'Six bedrooms minimum. Modern preferred, but open to classical. Three-day visit in two weeks.',
      },
      {
        at: daysAgo(6),
        from: 'ai',
        text: 'Perfect. Our founder Marco Rivera personally handles waterfront estates above $10M. He will send a curated three-property tour plan within the hour. May I confirm arrival date?',
      },
      {
        at: daysAgo(5),
        from: 'lead',
        text: 'April 28. Arriving with my husband. Hotel at Faena Miami Beach.',
      },
      {
        at: daysAgo(5),
        from: 'ai',
        text: 'Noted. Marco will have a driver coordinate all three properties and meet you at Faena on the 28th. Tour details incoming by email.',
      },
    ],
  },

  // ── CARLOS MENDEZ · Spanish · SMS ─────────────────────────────────────────
  {
    id: 'conv-carlos-01',
    leadId: 'lead-carlos-mendez',
    channel: 'sms',
    language: 'es',
    startedAt: daysAgo(4),
    endedAt: daysAgo(3),
    outcome: 'booked_showing',
    summary:
      'Carlos confirmó visita a Brickell viernes. Efectivo $3.5-5.5M. Interés en Cipriani y Casa Bella. Asignado a Diego Herrera.',
    messages: [
      {
        at: daysAgo(4),
        from: 'lead',
        text: 'Hola, estoy viajando a Miami este viernes. Quiero ver opciones en Brickell, preferentemente Cipriani o Casa Bella.',
      },
      {
        at: daysAgo(4),
        from: 'ai',
        text: '¡Bienvenido Carlos! Perfecto que vea Cipriani y Casa Bella, son dos de nuestras mejores torres en preventa. ¿Cuál es su presupuesto y cuántas recámaras necesita?',
      },
      {
        at: daysAgo(4),
        from: 'lead',
        text: 'Entre $4 y $5.5 millones. Tres recámaras. Pago en efectivo.',
      },
      {
        at: daysAgo(4),
        from: 'ai',
        text: 'Excelente. ¿Le gustaría también ver una unidad lista para entregar en 1000 Biscayne? Tenemos una residencia de 4 recámaras a $7.25M que vale la comparación.',
      },
      {
        at: daysAgo(4),
        from: 'lead',
        text: 'Sí, agréguela al recorrido.',
      },
      {
        at: daysAgo(3),
        from: 'ai',
        text: 'Listo. Diego Herrera, nuestro especialista en Brickell, le recibirá el viernes. Le envío el itinerario con horarios: 10am Cipriani, 12pm Casa Bella, 2pm 1000 Biscayne. Almuerzo incluido.',
      },
      {
        at: daysAgo(3),
        from: 'lead',
        text: 'Confirmado. Gracias.',
      },
    ],
  },

  // ── EMILY WATSON · English · webchat with AI → HUMAN ESCALATION ──────────
  {
    id: 'conv-emily-01',
    leadId: 'lead-emily-watson',
    channel: 'webchat',
    language: 'en',
    startedAt: daysAgo(3),
    endedAt: daysAgo(3),
    outcome: 'escalated_to_human',
    escalation: {
      reason: 'price_negotiation',
      escalatedAt: daysAgo(3),
      escalatedToAgentId: 'agent-natalia-ortiz',
      trigger:
        'Lead asked whether seller would entertain a 12% price reduction — outside the AI’s authority threshold. Auto-handoff to assigned agent.',
    },
    summary:
      'Emily asked about schools and a material price reduction on a Grove listing. AI handled schools; handed off pricing to Natalia Ortiz.',
    messages: [
      {
        at: daysAgo(3),
        from: 'lead',
        text: 'Hi — interested in 3541 Main Hwy in Coconut Grove. How is it for schools if we relocate with a 9-year-old?',
      },
      {
        at: daysAgo(3),
        from: 'ai',
        text: 'Great question. Coconut Grove is zoned for Coconut Grove Elementary (A-rated). Ransom Everglades and Carrollton School are two top private options within 10 minutes.',
      },
      {
        at: daysAgo(3),
        from: 'lead',
        text: 'Helpful. Between us — is the seller flexible? We would consider $4.5M all-in.',
      },
      {
        at: daysAgo(3),
        from: 'ai',
        text: "That's a meaningful offer relative to ask. I'm going to bring in Natalia Ortiz, our Grove specialist, so you can discuss terms directly. She'll be with you in under 2 minutes.",
      },
      {
        at: daysAgo(3),
        from: 'agent',
        agentId: 'agent-natalia-ortiz',
        text: 'Hi Emily — Natalia here. Happy to walk through what the seller has told us so far. Would a 15-minute call today at 4pm work?',
      },
      {
        at: daysAgo(3),
        from: 'lead',
        text: '4pm works.',
      },
      {
        at: daysAgo(3),
        from: 'agent',
        agentId: 'agent-natalia-ortiz',
        text: 'Perfect — calling you at 4pm ET. I\'ll have recent comps and HOA breakdown ready.',
      },
    ],
  },

  // ── MARIA LOPEZ · Spanish · SMS ──────────────────────────────────────────
  {
    id: 'conv-maria-01',
    leadId: 'lead-maria-lopez',
    channel: 'sms',
    language: 'es',
    startedAt: daysAgo(14),
    endedAt: daysAgo(1),
    outcome: 'nurturing',
    summary:
      'María necesita vender su casa de Doral primero. IA agendó referencia de lender y check-in cada 2 semanas.',
    messages: [
      {
        at: daysAgo(14),
        from: 'lead',
        text: 'Hola, me interesa Cipriani. Tengo que vender mi casa primero.',
      },
      {
        at: daysAgo(14),
        from: 'ai',
        text: '¡Hola María! No hay problema. ¿Ya tiene su casa listada? Podemos ayudar con el listing y coordinar el contingent.',
      },
      {
        at: daysAgo(14),
        from: 'lead',
        text: 'Planeo listarla en 30 días.',
      },
      {
        at: daysAgo(14),
        from: 'ai',
        text: 'Perfecto. Le enviamos contactos de lenders para pre-aprobación condicional, así tenemos ambas transacciones alineadas.',
      },
      {
        at: daysAgo(1),
        from: 'ai',
        text: 'Hola María, pasando a saludar. ¿Ya listó la casa o quiere que le presentemos nuestro equipo de listing?',
      },
    ],
  },

  // ── ROBERTO SILVA · Portuguese · whatsapp ────────────────────────────────
  {
    id: 'conv-roberto-01',
    leadId: 'lead-roberto-silva',
    channel: 'whatsapp',
    language: 'pt',
    startedAt: daysAgo(27),
    endedAt: daysAgo(4),
    outcome: 'qualified',
    summary:
      'Roberto busca condo em Brickell para investimento + uso parcial. $2-3.5M em dinheiro. Prédio precisa permitir short-term rentals.',
    messages: [
      {
        at: daysAgo(27),
        from: 'lead',
        text: 'Oi, quero informações sobre Cipriani Residences. Uso parte tempo e parte aluguel.',
      },
      {
        at: daysAgo(27),
        from: 'ai',
        text: 'Olá Roberto! Cipriani permite aluguel mínimo de 30 dias. Há outras torres com política mais flexível — posso mandar comparativo?',
      },
      {
        at: daysAgo(27),
        from: 'lead',
        text: 'Sim, por favor. Orçamento $2-3.5M em dinheiro.',
      },
      {
        at: daysAgo(4),
        from: 'ai',
        text: 'Olá Roberto, Diego (nosso especialista em Brickell) vai ligar amanhã às 11am para alinhar tour virtual.',
      },
    ],
  },

  // ── ALEXANDER VOLKOV · English · webchat (RED FLAG) ──────────────────────
  {
    id: 'conv-volkov-01',
    leadId: 'lead-alexander-volkov',
    channel: 'webchat',
    language: 'en',
    startedAt: daysAgo(3),
    endedAt: hoursAgo(12),
    outcome: 'flagged_for_review',
    summary:
      'Volkov claims $8.5M cash, urgent Moscow relocation, CEO role. AI detected multiple inconsistencies — unverifiable company, missing public profile, phone area code mismatch. Auto-paused, held for manual review.',
    messages: [
      {
        at: daysAgo(3),
        from: 'lead',
        text: 'I need to buy $8.5M property in Miami Beach. Moving from Moscow urgently. Close in 30 days.',
      },
      {
        at: daysAgo(3),
        from: 'ai',
        text: 'Welcome. To match inventory I need a few details — is this a primary or secondary residence, and how many bedrooms?',
      },
      {
        at: daysAgo(3),
        from: 'lead',
        text: 'Primary. 5 bedrooms minimum. Ocean view. I am CEO of holding company.',
      },
      {
        at: daysAgo(3),
        from: 'ai',
        text: 'Understood. For offers this size, sellers typically ask for a proof-of-funds letter. Can you share your banking institution?',
      },
      {
        at: daysAgo(3),
        from: 'lead',
        text: 'I will provide at closing. Not before.',
      },
      {
        at: daysAgo(3),
        from: 'ai',
        text: 'Most Miami Beach sellers require POF before a private tour. We can introduce our compliance team to handle this confidentially.',
      },
      {
        at: hoursAgo(36),
        from: 'lead',
        text: 'No compliance. Private. Urgent. Schedule tour now.',
      },
      {
        at: hoursAgo(12),
        from: 'ai',
        text: 'I need to pause this conversation so our team can review. A senior advisor will reach out within 24 hours.',
      },
    ],
  },
];

export const conversationsByLeadId = conversations.reduce((acc, c) => {
  (acc[c.leadId] = acc[c.leadId] || []).push(c);
  return acc;
}, {});

// Utility: Unused import guard for future tooling.
void minutesAgo;
